import * as Phaser from "phaser-ce"
import { CCUtils } from "../../../common/CCUtils"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { ColorUtils } from "../../utils/ColorUtils"
import { PhaserUtils } from "../../utils/PhaserUtils"
import {
    EcigTourAssets,
    EcigTourAudio,
    loadAssets,
} from "./ecig-tour/EcigTourAssets"
import { ReviewQuestion } from "./ReviewQuestion"
import { TobaccoTourProgressStore } from "./TobaccoTourProgressStore"

export class TobaccoTourReviewState extends Phaser.State {
    private readonly trueFalseAnswers = ["True", "False"]
    private readonly trueFalseAudio = [EcigTourAudio.TRUE, EcigTourAudio.FALSE]

    private static get encouragements(): Array<string> {
        const encouragements = [
            EcigTourAssets.GOOD_JOB,
            EcigTourAssets.THATS_CORRECT,
            EcigTourAssets.YOURE_RIGHT,
        ]
        const extras1 = CCUtils.randomNumber(3)
        const extras2 = CCUtils.randomNumber(3)
        encouragements[3] = encouragements[extras1]
        encouragements[4] = encouragements[extras2]
        CCUtils.shuffle(encouragements)
        return encouragements
    }

    preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setBackgroundColorGray(this.game)

        ReviewQuestion.askQuestions(this.questions(), this.completeReview)
    }

    private questions = (): Array<ReviewQuestion> => [
        new ReviewQuestion(
            this.game,
            "Nicotine is the chemical in tobacco that makes you become addicted:",
            this.trueFalseAnswers,
            0,
            TobaccoTourReviewState.encouragements[0],
            EcigTourAudio.REVIEW_QUESTION_1,
            this.trueFalseAudio,
        ),
        new ReviewQuestion(
            this.game,
            "Acetaldehyde is a poisonous chemical that is used to:",
            [
                "Strip paint",
                "Flavor ice cream",
                "Preserve pickles",
                "Clean teeth",
            ],
            0,
            TobaccoTourReviewState.encouragements[1],
            EcigTourAudio.REVIEW_QUESTION_2,
            [
                EcigTourAudio.REVIEW_QUESTION_2a,
                EcigTourAudio.REVIEW_QUESTION_2b,
                EcigTourAudio.REVIEW_QUESTION_2c,
                EcigTourAudio.REVIEW_QUESTION_2d,
            ],
        ),
        new ReviewQuestion(
            this.game,
            "If you inhale cadmium, it can cause:",
            [
                "Lung irritation",
                "Kidney disease",
                "Lung cancer",
                "All of the above",
            ],
            3,
            TobaccoTourReviewState.encouragements[2],
            EcigTourAudio.REVIEW_QUESTION_3,
            [
                EcigTourAudio.REVIEW_QUESTION_3a,
                EcigTourAudio.REVIEW_QUESTION_3b,
                EcigTourAudio.REVIEW_QUESTION_3c,
                EcigTourAudio.REVIEW_QUESTION_3d,
            ],
            true,
        ),
        new ReviewQuestion(
            this.game,
            "Propylene Glycol can cause asthma and other breathing problems:",
            this.trueFalseAnswers,
            0,
            TobaccoTourReviewState.encouragements[3],
            EcigTourAudio.REVIEW_QUESTION_4,
            this.trueFalseAudio,
        ),
        new ReviewQuestion(
            this.game,
            "Cigarettes contain smoke and chemicals that can cause:",
            ["Gum Disease", "Heart Disease", "Lung Cancer", "All of the above"],
            3,
            TobaccoTourReviewState.encouragements[4],
            EcigTourAudio.REVIEW_QUESTION_5,
            [
                EcigTourAudio.REVIEW_QUESTION_5a,
                EcigTourAudio.REVIEW_QUESTION_5b,
                EcigTourAudio.REVIEW_QUESTION_5c,
                EcigTourAudio.REVIEW_QUESTION_5d,
            ],
            true,
        ),
        new ReviewQuestion(
            this.game,
            "Formaldehyde damages mouth, stomach, and lung tissue:",
            this.trueFalseAnswers,
            0,
            TobaccoTourReviewState.encouragements[0],
            EcigTourAudio.REVIEW_QUESTION_6,
            this.trueFalseAudio,
        ),
        new ReviewQuestion(
            this.game,
            "Arsenic can cause your lips to burn, bad breath, and in large amounts, death. " +
                "It is found in tobacco and in which of the following: ",
            ["Chewing gum", "Apples", "Rat poison", "Balloons"],
            2,
            TobaccoTourReviewState.encouragements[1],
            EcigTourAudio.REVIEW_QUESTION_7,
            [
                EcigTourAudio.REVIEW_QUESTION_7a,
                EcigTourAudio.REVIEW_QUESTION_7b,
                EcigTourAudio.REVIEW_QUESTION_7c,
                EcigTourAudio.REVIEW_QUESTION_7d,
            ],
        ),
        new ReviewQuestion(
            this.game,
            "Carbon Monoxide is a colorless, odorless gas found in lit cigarettes that can: ",
            [
                "Make you laugh",
                "Cause indigestion",
                "Help heal blisters",
                "Make you dizzy",
            ],
            3,
            TobaccoTourReviewState.encouragements[2],
            EcigTourAudio.REVIEW_QUESTION_8,
            [
                EcigTourAudio.REVIEW_QUESTION_8a,
                EcigTourAudio.REVIEW_QUESTION_8b,
                EcigTourAudio.REVIEW_QUESTION_8c,
                EcigTourAudio.REVIEW_QUESTION_8d,
            ],
        ),
        new ReviewQuestion(
            this.game,
            "When mixed with nicotine, ammonia helps nicotine to be absorbed faster. It also causes: ",
            ["Skin irritation", "Lung damage", "Coughing", "All of the above"],
            3,
            TobaccoTourReviewState.encouragements[3],
            EcigTourAudio.REVIEW_QUESTION_9,
            [
                EcigTourAudio.REVIEW_QUESTION_9a,
                EcigTourAudio.REVIEW_QUESTION_9b,
                EcigTourAudio.REVIEW_QUESTION_9c,
                EcigTourAudio.REVIEW_QUESTION_9d,
            ],
            true,
        ),
        new ReviewQuestion(
            this.game,
            "The chemical in tobacco that's like exposing your body to tiny atomic bombs is: ",
            ["Hydrogen", "Polonium 210", "Oxygen", "Carbon"],
            1,
            TobaccoTourReviewState.encouragements[4],
            EcigTourAudio.REVIEW_QUESTION_10,
            [
                EcigTourAudio.REVIEW_QUESTION_10a,
                EcigTourAudio.REVIEW_QUESTION_10b,
                EcigTourAudio.REVIEW_QUESTION_10c,
                EcigTourAudio.REVIEW_QUESTION_10d,
            ],
        ),
    ]

    private completeReview = () => {
        TobaccoTourProgressStore.instance.setReviewComplete()
        this.game.state.start(ActivityKey.TOBACCO_TOUR)
    }
}
